import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BarbellOutline, Close, TimeOutline } from './SharedWorkoutIcons';
import { API_BASE } from '../api';
import SharedWorkoutBody, { SharedWorkoutPayload, VideoTarget } from './SharedWorkoutBody';
import '../styles/shared-workout.css';

const APP_STORE_URL = 'https://apps.apple.com/us/app/burst-fitness/id6739505525';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.burst.fitness';

/**
 * The app's activity colours (`Colours` in apps/mobile/client/app/src/components/styles.js),
 * as WorkoutCardHeader picks them for the card's side strip.
 */
function colourForActivityType(at: string | null | undefined): string {
    const v = (at || '').toLowerCase();
    if (v === 'gym' || v === 'strength') return '#A796A0';
    if (v === 'running' || v === 'run' || v === 'easy' || v === 'tempo' || v === 'intervals' || v === 'long') return '#F2D28E';
    if (v === 'mobility') return '#C2D7EA';
    if (v === 'hiit') return '#947D6F';
    if (v === 'hyrox') return '#CA6B45';
    if (v === 'erg' || v === 'rowing') return '#2F4858';
    if (v === 'stair') return '#B5838D';
    if (v === 'other') return '#8B9DAF';
    return '#FFFFFF';
}

/** The app's WorkoutCardHeader: colour strip, title, duration and type boxes. */
function CardHeader({ data }: { data: SharedWorkoutPayload }) {
    return (
        <div className="sw-overview">
            <div className="sw-overview-strip" style={{ backgroundColor: colourForActivityType(data.activity_type) }} aria-hidden />
            <div className="sw-overview-box">
                <h2 className="sw-overview-title">{data.name || 'Shared workout'}</h2>
                <div className="sw-overview-boxes">
                    <div className="sw-training-box">
                        <span className="sw-training-label">DURATION</span>
                        <span className="sw-training-value">
                            <TimeOutline size={12} />
                            {data.duration ? `${Math.round(data.duration)} mins` : 'TBD'}
                        </span>
                    </div>
                    <div className="sw-training-box">
                        <span className="sw-training-label">TYPE</span>
                        <span className="sw-training-value">
                            <BarbellOutline size={12} />
                            {data.activity_type || 'Workout'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** A movement or technique clip, or a technique's cues when it has no clip (the app's video modals). */
function VideoModal({ video, onClose }: { video: VideoTarget | null; onClose: () => void }) {
    useEffect(() => {
        if (!video) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [video, onClose]);
    if (!video) return null;
    return (
        <div className="sw-modal" role="dialog" aria-modal="true" aria-label={video.title} onClick={onClose}>
            <div className="sw-modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="sw-modal-head">
                    <h3 className="sw-modal-title">{video.title}</h3>
                    <button type="button" className="sw-modal-close" onClick={onClose} aria-label="Close">
                        <Close size={22} />
                    </button>
                </div>
                {video.url ? (
                    <video className="sw-modal-video" src={video.url} controls autoPlay playsInline />
                ) : null}
                {video.cues ? <p className="sw-modal-cues">{video.cues}</p> : null}
            </div>
        </div>
    );
}

const SharedWorkout: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [data, setData] = useState<SharedWorkoutPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [video, setVideo] = useState<VideoTarget | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!token) {
                setError('Missing share token.');
                setLoading(false);
                return;
            }
            try {
                const res = await fetch(`${API_BASE}/saved_workouts/shared/${encodeURIComponent(token)}/`);
                if (res.status === 404) {
                    if (!cancelled) setError('This shared workout is no longer available.');
                } else if (!res.ok) {
                    if (!cancelled) setError('Could not load this workout.');
                } else {
                    const json: SharedWorkoutPayload = await res.json();
                    if (!cancelled) setData(json);
                }
            } catch {
                if (!cancelled) setError('Could not load this workout.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    return (
        <div className="sw-root">
            <header className="sw-header">
                <a className="sw-brand-link" href="/">Burst</a>
            </header>

            <main className="sw-main">
                {loading && <p className="sw-loading">Loading workout…</p>}

                {!loading && error && (
                    <div className="sw-empty">
                        <h1>Workout unavailable</h1>
                        <p>{error}</p>
                        <p className="sw-hint">The link may have been removed by its owner.</p>
                    </div>
                )}

                {!loading && !error && data && (
                    <>
                        <h1 className="sw-heading">{data.name || 'Shared workout'}</h1>

                        <section className="sw-card" aria-label="Workout">
                            <CardHeader data={data} />
                            <div className="sw-divider" />
                            <div className="sw-list">
                                <SharedWorkoutBody data={data} openVideo={setVideo} />
                            </div>

                            <div className="sw-cta">
                                <h2 className="sw-cta-title">Save this to Burst</h2>
                                <p className="sw-cta-body">
                                    Open Burst on your phone to save this workout and start training with it.
                                </p>
                                <div className="sw-store-row">
                                    <a className="sw-store-badge" href={APP_STORE_URL} target="_blank" rel="noreferrer" aria-label="Download on the App Store">
                                        <img
                                            src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?releaseDate=1700611200"
                                            alt="Download on the App Store"
                                            className="sw-badge-img sw-badge-img--apple"
                                        />
                                    </a>
                                    <a className="sw-store-badge sw-store-badge--google" href={PLAY_STORE_URL} target="_blank" rel="noreferrer" aria-label="Get it on Google Play">
                                        <img
                                            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                                            alt="Get it on Google Play"
                                            className="sw-badge-img sw-badge-img--google"
                                        />
                                    </a>
                                </div>
                            </div>
                        </section>
                    </>
                )}
            </main>

            <VideoModal video={video} onClose={() => setVideo(null)} />
        </div>
    );
};

export default SharedWorkout;
